// MedConfig keys (Spec List 12; build plan §7.1 #1): what each key holds, who
// may edit it (plan §5), whether go-live needs it, and the one validator every
// save goes through. Pure, so the SPA renders its per-key editors from these
// same definitions (as Account.tsx uses ../server/phone).
//
// Who edits what (plan §5):
//   config.operational (System Admin, Supervisor, RN): Windows, Cities,
//     HeldRefusedReasons, and ReceivedExceptionCodes (a vocabulary like
//     held/refused — a reading, recorded in plan §7.5).
//   config.admin (System Admin): everything else — insurance, contacts,
//     signer, dosing authority (named in §5), and the keys §5 does not name
//     (session dates, notification rules, digest, provider role, sender,
//     the roster schedule, the scheduled emails).
//
// The medical advisor appears only as a ROLE with contact routes (plan §0.5):
// Contacts.remoteProvider has no name field, and the standing order records
// the signer's role and credential, never a person's name.

import type { Capability } from "../roles.js";
import { firstOverlap } from "../station/windows.js";
import { TEMPLATE_DEFS, TEMPLATE_KEYS, TEMPLATE_STATUSES } from "./templates.js";

export type FieldKind = "text" | "paragraph" | "email" | "phone" | "date" | "time" | "url" | "number" | "yn" | "choice" | "lines" | "group" | "rows";

export interface Field {
  name: string;
  label: string;
  kind: FieldKind;
  /** Must be filled when the key is saved (not blank). */
  required?: boolean;
  hint?: string;
  /** "choice" only. */
  options?: readonly string[];
  /** "group" (one object) and "rows" (a list of objects). */
  fields?: readonly Field[];
  /** "lines": each line must be an email address. */
  linesAreEmails?: boolean;
}

export interface ConfigKeyDef {
  key: string;
  label: string;
  /** One sentence for the editor. */
  help: string;
  cap: Extract<Capability, "config.admin" | "config.operational">;
  /** Blank blocks go-live (the readiness banner lists it). */
  requiredForGoLive: boolean;
  /** The value's shape: a single "lines"/"rows" field holds a bare array; otherwise an object. */
  shape: "object" | "lines" | "rows";
  fields: readonly Field[];
  /** The value a blank row is seeded with on first run (M3 Stage 2), per program; none = blank. */
  seed?: (programShort: string) => unknown;
  /**
   * "rows" keys whose rows are never removed (M3 Stage 3, Windows): a save
   * must keep every row already stored, by this field (case-insensitive);
   * a row is retired instead, so the history that names it still reads.
   */
  keepRowsBy?: string;
}

/** The attestation sentence a parent or guardian agrees to for a medication added at the med station (M3 Stage 2). */
export function defaultAttestationText(programShort: string): string {
  const child = programShort === "MDGS" ? "daughter" : "son";
  return `I confirm this medication was prescribed/recommended for my ${child} and I ask the program to administer it as labeled`;
}

/** The attendee's own attestation (18 or older: Relationship=Self). */
export const SELF_ATTESTATION_TEXT = "I confirm this medication was prescribed/recommended for me and I ask the program to administer it as labeled";

/** The held/refused vocabulary a blank Config row is seeded with on first run (Decisions 2 #19); staff edit it. */
export const DEFAULT_HELD_REFUSED_REASONS = [
  "Student refused",
  "Vomiting or unable to take by mouth",
  "Held on provider or RN instruction",
  "Medication not available",
  "Student not present for the window",
  "Parent asked to hold",
] as const;

/** The Reset screen's starting points (test-env/packs; pack-roles.test.ts holds this list to the registry), newest first. */
export const TEST_PACK_NAMES = ["after-the-session", "session-day-3", "session-day", "check-in-day", "pre-session-review", "clean-start"] as const;
/** The one the guide names: "Recommended for testing" on the Reset screen unless Config says otherwise. */
export const DEFAULT_TEST_RECOMMENDED_PACK = "session-day";

/** The allergy / anaphylaxis action plan form a family's provider may complete (owner decision 2026-10-16): linked, never hosted. */
export const DEFAULT_ALLERGY_PLAN_TEMPLATE_URL = "https://www.foodallergy.org/resources/food-allergy-anaphylaxis-emergency-care-plan";

/** The program-standard steps an allergy response note starts from (Config AllergyResponseStandard). */
export const DEFAULT_ALLERGY_RESPONSE_STANDARD =
  "Program standard: if exposed and there are any signs of a severe reaction (trouble breathing, throat tightness, widespread hives, vomiting, faintness), give the epinephrine auto-injector at once and call 911. Then call the med station and the student's parent or guardian. Keep the student lying down (sitting up if breathing is hard) and stay with them. A second dose may be given after 5 minutes if symptoms continue and a second auto-injector is available. For mild symptoms only (a few hives, itchy mouth), bring the student to the med station and watch closely for worsening.";

/** Hours a medication may wait for authorization before the queue marks it (Config PendingAuthorizationAlertHours, default). */
export const DEFAULT_PENDING_ALERT_HOURS = 4;

const NOTE: Field = { name: "note", label: "Note", kind: "text", hint: "Optional, for staff." };
const phone = (name: string, label: string, required = false): Field => ({ name, label, kind: "phone", required });
const place = (name: string, label: string, withAddress = false): Field => ({
  name,
  label,
  kind: "group",
  fields: [
    { name: "name", label: "Name", kind: "text", required: true },
    phone("phone", "Phone", true),
    ...(withAddress ? [{ name: "address", label: "Address", kind: "text" } as Field] : []),
  ],
});

/** Every Spec §2 ConfigKey, in Spec order, then this app's own scheduling key. */
export const CONFIG_KEY_DEFS: readonly ConfigKeyDef[] = [
  {
    key: "SessionDates",
    label: "Session dates",
    help: "The session week. Check-in day is when \"18 at check-in\" is measured.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "start", label: "First day", kind: "date", required: true },
      { name: "end", label: "Last day", kind: "date", required: true },
      { name: "checkInOn", label: "Check-in day", kind: "date", required: true },
      { name: "nextSessionStartOn", label: "Next year's first day", kind: "date", hint: "For the supplies expiry rule." },
      NOTE,
    ],
  },
  {
    key: "Windows",
    label: "Medication windows",
    help: "The year's medication times. Staff set these each year. Windows may not overlap. A window is never deleted: retire it, and its history keeps its name.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "rows",
    keepRowsBy: "code",
    fields: [
      { name: "code", label: "Code", kind: "text", required: true, hint: "Short, e.g. AM. Fixed once saved." },
      { name: "name", label: "Name", kind: "text", required: true },
      { name: "startTime", label: "Starts", kind: "time", required: true },
      { name: "endTime", label: "Ends", kind: "time", required: true },
      { name: "sortOrder", label: "Sort order", kind: "number", hint: "Lower first. Blank: by start time." },
      { name: "retired", label: "Retired", kind: "yn", hint: "Yes: no longer offered; its history stays." },
    ],
  },
  {
    key: "Cities",
    label: "Cities",
    help: "This year's city names, one per line.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
  },
  {
    key: "OtcDosingAuthority",
    label: "OTC dosing authority",
    help: "Where OTC doses come from: a signed standing order, or the product label.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "kind", label: "Source", kind: "choice", options: ["StandingOrder", "ProductLabel"], required: true },
      { name: "signerRole", label: "Standing order signed by (role)", kind: "text", hint: "A role, never a name." },
      { name: "credential", label: "Signer's credential", kind: "text" },
      { name: "signedOn", label: "Signed on", kind: "date" },
      { name: "docKey", label: "Document key", kind: "text", hint: "The signed order's MedDocuments key, once attached." },
      NOTE,
    ],
  },
  {
    key: "HeldRefusedReasons",
    label: "Held / refused reasons",
    help: "The choices staff pick from when a dose is held or refused, one per line (a reason is mandatory; staff may add a note).",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
    // Decisions 2 #19: mandatory, from a staff-editable vocabulary plus free text. A starting list staff edit.
    seed: () => [...DEFAULT_HELD_REFUSED_REASONS],
  },
  {
    key: "ReceivedExceptionCodes",
    label: "Medication receipt exceptions",
    help: "The exceptions recorded when medication arrives, one per line.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
  },
  {
    key: "Insurance",
    label: "Insurance",
    help: "The program's accident policy and its claims administrator.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "policyNumber", label: "Policy number", kind: "text", required: true },
      { name: "policyholderName", label: "Policyholder", kind: "text", required: true },
      { name: "policyholderAddress", label: "Policyholder address", kind: "text", hint: "The street line (claim form Part I, 3)." },
      // M5: claim form Part I 4–5 (the city, state and zip have their own boxes; the fax is the policyholder's).
      { name: "policyholderCity", label: "Policyholder city", kind: "text" },
      { name: "policyholderState", label: "Policyholder state", kind: "text" },
      { name: "policyholderZip", label: "Policyholder zip", kind: "text" },
      phone("policyholderFax", "Policyholder fax"),
      { name: "administratorName", label: "Claims administrator", kind: "text", required: true },
      { name: "administratorEmail", label: "Administrator email", kind: "email" },
      phone("administratorFax", "Administrator fax"),
      phone("administratorPhone", "Administrator phone"),
      { name: "noticeDays", label: "Notice period (days)", kind: "number", hint: "Usually 90." },
      NOTE,
    ],
  },
  {
    key: "ClaimSigner",
    label: "Claim form signer",
    help: "Who signs Part I of the claim form for this program.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "name", label: "Name", kind: "text", required: true },
      { name: "title", label: "Title", kind: "text", required: true },
      phone("phone", "Phone", true),
      // M5: claim form Part I 5 (blank: the Director's email from Contacts).
      { name: "email", label: "Email", kind: "email" },
      NOTE,
    ],
  },
  {
    key: "Contacts",
    label: "Contacts",
    help: "Who staff call, and where the director's mail goes.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      place("campusResponders", "Campus first responders"),
      place("localEms", "Local EMS"),
      {
        name: "remoteProvider",
        label: "Remote medical provider",
        kind: "group",
        hint: "By role only, never a name.",
        fields: [
          { name: "role", label: "Role", kind: "text", required: true },
          phone("phone", "Phone"),
          { name: "email", label: "Email", kind: "email" },
          { name: "teamsLink", label: "Teams link", kind: "url" },
        ],
      },
      place("urgentCare", "Urgent care", true),
      place("hospital", "Hospital", true),
      phone("medStationPhone", "Med station phone"),
      { name: "directorEmail", label: "Director's email", kind: "email", required: true },
      NOTE,
    ],
  },
  {
    key: "NotificationRules",
    label: "Notification rules",
    help: "When parent notifications are due, and which dispositions need a phone call.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "emailDueHours", label: "Email due within (hours)", kind: "number", required: true, hint: "Usually 24." },
      { name: "phoneTierDispositions", label: "Dispositions that need a phone call", kind: "lines" },
      NOTE,
    ],
  },
  {
    key: "DocRequestDigest",
    label: "Document request digest",
    help: "When a document request counts as overdue, and who gets the daily digest.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "olderThanDays", label: "Overdue after (days)", kind: "number", required: true, hint: "Suggested 7." },
      { name: "recipients", label: "Digest recipients", kind: "lines", linesAreEmails: true },
      NOTE,
    ],
  },
  {
    key: "ProviderRole",
    label: "Provider coverage",
    help: "Whether an on-site Supervisor is present and a remote provider is engaged.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "onSiteSupervisorPresent", label: "On-site Supervisor present", kind: "yn", required: true },
      { name: "remoteProviderEngaged", label: "Remote provider engaged", kind: "yn", required: true },
      NOTE,
    ],
  },
  {
    key: "MailFrom",
    label: "Sender address",
    help: "The program's no-reply address for this app's email.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [{ name: "address", label: "Address", kind: "email", required: true }, NOTE],
  },
  {
    key: "RosterSweep",
    label: "Roster schedule",
    help: "Read the applications into the roster automatically every 15 minutes. Off unless set to Yes.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "scheduled", label: "Read automatically", kind: "yn", required: true }, NOTE],
  },
  {
    key: "ScheduledJobs",
    label: "Scheduled emails",
    help: "Document-request reminders (checked every 15 minutes) and the director's daily digest (07:00 Eastern). Blank means on in the live app and off in the test app.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [
      { name: "docRequestReminders", label: "Send document-request reminders", kind: "yn" },
      { name: "directorDigest", label: "Send the director's daily digest", kind: "yn" },
      { name: "directorReport", label: "Send the Director's report (#13) at its two times", kind: "yn" },
      NOTE,
    ],
  },
  {
    key: "TestAmendmentsClose",
    label: "Amendments close (test app only)",
    help: "TEST APP ONLY: the day families' amendments close, so the medication seed can be frozen. The live app ignores this and uses AMENDMENTS_CLOSE_DATE, the same date as the portals.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "closedOn", label: "Amendments closed on", kind: "date", required: true }, NOTE],
  },
  {
    key: "TestRecommendedPack",
    label: "Recommended Reset starting point (test app only)",
    help: "TEST APP ONLY: the starting point the Reset screen marks “Recommended for testing” (the one the guide names); the others are under More starting points. Blank means session-day. Every Reset keeps this setting. The live app has no Reset.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "pack", label: "Starting point", kind: "choice", options: TEST_PACK_NAMES, required: true }, NOTE],
  },
  {
    key: "PendingAuthorizationAlertHours",
    label: "Pending authorization alert",
    help: "A medication waiting for authorization longer than this is marked in the pending queue and listed in the director's digest. Blank means 4 hours.",
    cap: "config.operational",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "hours", label: "Alert after (hours)", kind: "number", required: true }, NOTE],
    seed: () => ({ hours: DEFAULT_PENDING_ALERT_HOURS }),
  },
  {
    key: "ParentAttestationText",
    label: "Parent attestation (added medication)",
    help: "What a parent or guardian agrees to when a medication not on the application is added at the med station. Blank means the default sentence.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "text", label: "Attestation", kind: "text", required: true }, NOTE],
    seed: (programShort) => ({ text: defaultAttestationText(programShort) }),
  },
  {
    key: "AllergyPlanTemplateUrl",
    label: "Allergy action plan form (link)",
    help: "The blank allergy / anaphylaxis action plan a family's provider can complete, linked (never attached) in the action-plan request email.",
    cap: "config.operational",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "url", label: "Link", kind: "url", required: true }, NOTE],
    seed: () => ({ url: DEFAULT_ALLERGY_PLAN_TEMPLATE_URL }),
  },
  {
    key: "AllergyResponseStandard",
    label: "Allergy response: steps if exposed",
    help: "The program-standard steps the allergy response note starts from. The RN edits them here, or per student.",
    cap: "config.operational",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "text", label: "Steps if exposed", kind: "paragraph", required: true }, NOTE],
    seed: () => ({ text: DEFAULT_ALLERGY_RESPONSE_STANDARD }),
  },
  {
    key: "DirectorReportTimes",
    label: "Director's report times",
    help: "When the Director's report (#13) is emailed to the Director, Eastern time. Blank means 07:00 and 17:30. Sent only when Scheduled emails says so (off in the test app).",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [
      { name: "morning", label: "Morning", kind: "time", required: true },
      { name: "dinner", label: "Dinner", kind: "time", required: true },
      NOTE,
    ],
  },
  // M4 Stage 2: the parent-notification templates (D2 §F; plan §13 #6), each its own key, seeded with the
  // shipped wording and "Draft" (RN review pending). The wording is edited here; only an RN sets Approved
  // (config-service.ts); any wording change returns an approved template to Draft.
  ...TEMPLATE_DEFS.map(
    (t): ConfigKeyDef => ({
      key: t.key,
      label: t.label,
      help: `${t.help} ${t.origin === "D2 §F" ? "Wording: D2 §F." : "New wording (D2 §F has none)."} [Brackets] are filled from the visit; any left must be completed before an email is sent.`,
      cap: "config.operational",
      requiredForGoLive: false,
      shape: "object",
      fields: [
        { name: "subject", label: "Subject", kind: "text", required: true },
        { name: "body", label: "Wording", kind: "paragraph", required: true },
        { name: "status", label: "Status", kind: "choice", options: TEMPLATE_STATUSES, required: true, hint: "Draft — RN review pending, until an RN sets Approved. Any change to approved wording returns it to Draft." },
        { name: "approvedBy", label: "Approved by", kind: "text", hint: "Set by the app when an RN approves." },
        { name: "approvedOn", label: "Approved on", kind: "date", hint: "Set by the app when an RN approves." },
        NOTE,
      ],
      seed: () => ({ subject: t.defaults.subject, body: t.defaults.body, status: "Draft" }),
    })
  ),
];


/** This app's own keys beside Spec §2's (plan §7.5.3, §7.5.5, §9.6, §10): never needed for go-live. */
export const APP_CONFIG_KEYS = ["RosterSweep", "ScheduledJobs", "TestAmendmentsClose", "TestRecommendedPack", "PendingAuthorizationAlertHours", "ParentAttestationText", "AllergyPlanTemplateUrl", "AllergyResponseStandard", "DirectorReportTimes", ...TEMPLATE_KEYS] as const;

/** The keys Spec §2 lists (the 13 the test packs fill). */
export const SPEC_CONFIG_KEYS = CONFIG_KEY_DEFS.filter((d) => !(APP_CONFIG_KEYS as readonly string[]).includes(d.key)).map((d) => d.key);

export function configKeyDef(key: string): ConfigKeyDef | null {
  return CONFIG_KEY_DEFS.find((d) => d.key === key) ?? null;
}

// ---- validation ------------------------------------------------------------------

export class ConfigValueError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigValueError";
  }
}

const MAX_TEXT = 500;
const MAX_PARAGRAPH = 2000;
const MAX_ITEMS = 100;
const RE = {
  email: /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/,
  phone: /^[0-9()+.\- ]{7,25}$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
  time: /^([01]\d|2[0-3]):[0-5]\d$/,
  url: /^https:\/\/[^\s<>"]+$/,
};

function realDate(s: string): boolean {
  if (!RE.date.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function cleanLines(v: unknown, label: string, emails: boolean): string[] {
  const raw = Array.isArray(v) ? v : typeof v === "string" ? v.split(/\r?\n/) : null;
  if (!raw) throw new ConfigValueError(`${label}: expected a list.`);
  const out = raw.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean);
  if (raw.some((x) => typeof x !== "string")) throw new ConfigValueError(`${label}: every line must be text.`);
  if (out.length > MAX_ITEMS) throw new ConfigValueError(`${label}: at most ${MAX_ITEMS} lines.`);
  for (const x of out) {
    if (x.length > MAX_TEXT) throw new ConfigValueError(`${label}: a line is too long.`);
    if (emails && !RE.email.test(x)) throw new ConfigValueError(`${label}: "${x}" is not an email address.`);
  }
  if (new Set(out.map((x) => x.toLowerCase())).size !== out.length) throw new ConfigValueError(`${label}: a line appears twice.`);
  return out;
}

function cleanScalar(f: Field, v: unknown, label: string): string | number | undefined {
  if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) {
    if (f.required) throw new ConfigValueError(`${label} is required.`);
    return undefined;
  }
  if (f.kind === "number") {
    const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.trim()) : NaN;
    if (!Number.isInteger(n) || n < 0 || n > 10_000) throw new ConfigValueError(`${label} must be a whole number.`);
    return n;
  }
  if (typeof v !== "string") throw new ConfigValueError(`${label} must be text.`);
  const s = v.trim();
  if (s.length > (f.kind === "paragraph" ? MAX_PARAGRAPH : MAX_TEXT)) throw new ConfigValueError(`${label} is too long.`);
  switch (f.kind) {
    case "email":
      if (!RE.email.test(s)) throw new ConfigValueError(`${label} must be an email address.`);
      return s.toLowerCase();
    case "phone":
      if (!RE.phone.test(s) || s.replace(/\D/g, "").length < 7) throw new ConfigValueError(`${label} must be a phone number.`);
      return s;
    case "date":
      if (!realDate(s)) throw new ConfigValueError(`${label} must be a date (YYYY-MM-DD).`);
      return s;
    case "time":
      if (!RE.time.test(s)) throw new ConfigValueError(`${label} must be a time (HH:MM, 24-hour).`);
      return s;
    case "url":
      if (!RE.url.test(s)) throw new ConfigValueError(`${label} must be an https:// link.`);
      return s;
    case "yn":
      if (s !== "Y" && s !== "N") throw new ConfigValueError(`${label} must be Y or N.`);
      return s;
    case "choice":
      if (!f.options?.includes(s)) throw new ConfigValueError(`${label} must be one of: ${f.options?.join(", ")}.`);
      return s;
    default:
      return s;
  }
}

function cleanObject(fields: readonly Field[], v: unknown, label: string): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) throw new ConfigValueError(`${label}: expected a set of fields.`);
  const src = v as Record<string, unknown>;
  const known = new Set(fields.map((f) => f.name));
  for (const k of Object.keys(src)) {
    if (!known.has(k)) throw new ConfigValueError(`${label}: unknown field "${k}".`);
  }
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const fl = `${label === "" ? "" : `${label} › `}${f.label}`;
    const val = src[f.name];
    if (f.kind === "group") {
      const empty = val === undefined || val === null || (typeof val === "object" && Object.values(val as object).every((x) => x === "" || x === null || x === undefined));
      if (empty) {
        if (f.required) throw new ConfigValueError(`${fl} is required.`);
        continue;
      }
      out[f.name] = cleanObject(f.fields ?? [], val, fl);
    } else if (f.kind === "rows") {
      out[f.name] = cleanRows(f.fields ?? [], val, fl);
    } else if (f.kind === "lines") {
      const lines = val === undefined || val === null ? [] : cleanLines(val, fl, Boolean(f.linesAreEmails));
      if (f.required && lines.length === 0) throw new ConfigValueError(`${fl} is required.`);
      if (lines.length) out[f.name] = lines;
    } else {
      const s = cleanScalar(f, val, fl);
      if (s !== undefined) out[f.name] = s;
    }
  }
  return out;
}

function cleanRows(fields: readonly Field[], v: unknown, label: string): Record<string, unknown>[] {
  if (!Array.isArray(v)) throw new ConfigValueError(`${label}: expected a list of rows.`);
  if (v.length > MAX_ITEMS) throw new ConfigValueError(`${label}: at most ${MAX_ITEMS} rows.`);
  return v.map((row, i) => cleanObject(fields, row, `${label} row ${i + 1}`));
}

/** Rules across fields, after each field is clean. */
function crossCheck(key: string, v: Record<string, unknown> | unknown[]): void {
  if (key === "SessionDates") {
    const d = v as Record<string, string>;
    if (d.end < d.start) throw new ConfigValueError("Session dates: the last day is before the first day.");
    if (d.checkInOn < d.start || d.checkInOn > d.end) throw new ConfigValueError("Session dates: check-in day must fall within the session.");
    if (d.nextSessionStartOn && d.nextSessionStartOn <= d.end) throw new ConfigValueError("Session dates: next year's first day must be after this session.");
  }
  if (key === "Windows") {
    const rows = v as Record<string, string>[];
    const codes = rows.map((r) => r.code.toUpperCase());
    if (new Set(codes).size !== codes.length) throw new ConfigValueError("Medication windows: each code must be different.");
    for (const r of rows) {
      if (!/^[A-Za-z0-9_-]{1,12}$/.test(r.code)) throw new ConfigValueError(`Medication windows: "${r.code}" — a code is 1 to 12 letters or digits.`);
      if (r.endTime <= r.startTime) throw new ConfigValueError(`Medication windows: ${r.code} ends before it starts.`);
    }
    const clash = firstOverlap(rows.map((r) => ({ code: r.code, startTime: r.startTime, endTime: r.endTime, retired: r.retired === "Y" })));
    if (clash) throw new ConfigValueError(`Medication windows: ${clash[0]} and ${clash[1]} overlap. Change a time, or retire one.`);
  }
  if (key === "OtcDosingAuthority") {
    const d = v as Record<string, string>;
    if (d.kind === "StandingOrder" && (!d.signerRole || !d.credential || !d.signedOn)) {
      throw new ConfigValueError("OTC dosing authority: a standing order needs the signer's role, credential and date.");
    }
  }
}

/**
 * Validates and normalizes a value for `key`. Returns the JSON to store, or ""
 * for blank (null, undefined or an empty list clears the key). Throws
 * ConfigValueError with a message fit to show staff.
 */
export function cleanConfigValue(key: string, value: unknown, previous?: unknown): string {
  const def = configKeyDef(key);
  if (!def) throw new ConfigValueError(`Unknown setting "${key}".`);
  if (def.keepRowsBy && Array.isArray(previous) && previous.length) {
    const by = def.keepRowsBy;
    const kept = new Set((Array.isArray(value) ? value : []).map((r) => String((r as Record<string, unknown>)?.[by] ?? "").trim().toUpperCase()));
    const gone = previous.map((r) => String((r as Record<string, unknown>)?.[by] ?? "").trim()).filter((c) => c && !kept.has(c.toUpperCase()));
    if (gone.length) throw new ConfigValueError(`${def.label}: ${gone.join(", ")} can't be removed or renamed. Retire it instead; its history keeps its name.`);
  }
  if (value === null || value === undefined || value === "") return "";
  let clean: Record<string, unknown> | unknown[];
  if (def.shape === "lines") {
    clean = cleanLines(value, def.label, false);
    if ((clean as string[]).length === 0) return "";
  } else if (def.shape === "rows") {
    clean = cleanRows(def.fields, value, def.label);
    if ((clean as unknown[]).length === 0) return "";
  } else {
    clean = cleanObject(def.fields, value, "");
  }
  crossCheck(key, clean);
  return JSON.stringify(clean);
}

/** The stored value parsed, or null when blank or unreadable. */
export function parseConfigValue(valueJson: string): unknown {
  if (!valueJson.trim()) return null;
  try {
    return JSON.parse(valueJson);
  } catch {
    return null;
  }
}
